import { AxiosResponse } from 'axios';
import { FilterOptions } from '../../hooks/useFilters';
import { RestServiceConnection } from '../restServiceConnection';

const ENDPOINT = '/tutor';

export class TutorService extends RestServiceConnection {

    listTutors = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list',
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }

    getTutorsForSelect = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-tutors-for-select',
            data: filters,
        }, true) as AxiosResponse;
        return this;
    }

    getTutorById = async (tutorId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/get',
            data: { tutorId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    linkTutorProductAssignment = async (tutorId: string, productId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/link-product-assignment',
            data: { tutorId, productId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    unlinkTutorProductAssignment = async (tutorId: string, productId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/unlink-product-assignment',
            data: { tutorId, productId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    toggleTutorStatus = async (tutorId: string, status: boolean) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/toggle',
            data: { tutorId, active: status },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    addTutorDocument = async (data: FormData) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/add-document',
            data: data,
            headers: {
                "Content-Type": "application/x-www-form-urlencoded"
            }
        }, true);
        return this;
    }

    createTutor = async (tutor: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/create',
            data: tutor,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    editTutor = async (tutor: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/edit',
            data: tutor,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    editTutorProfile = async (tutor: any) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/edit-profile',
            data: tutor,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    deleteTutor = async (tutorId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/delete',
            data: { tutorId },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    deleteMultiTutors = async (tutorsIds: string[]) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/delete-multi',
            data: { tutorsIds }
        }, true);
        return this;
    }

    changePassword = async (tutorId: string, password: string, passwordConfirm: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/change-password',
            data: { tutorId, password, passwordConfirm }
        }, true);
        return this;
    }

    loginAsTutor = async (tutorId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: '/users/login-as-tutor',
            data: { tutorId }
        }, true);
        return this;
    }

    listTutorDocuments = async (filters: FilterOptions) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-documents',
            data: filters,
        }, true);
        return this;
    }

    getTutorDocuments = async (tutorId: string, maxResults?: number | null) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/get-documents',
            data: { tutorId, maxResults },
        }, true);
        return this;
    }

    listChangelogs = async (tutorId: string, filters?: any) => {
        filters.filter_filters.tutor = tutorId;
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-tutor-changes-log',
            data: filters,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    listRolesByTutor = async (filters?: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/list-tutor-roles',
            data: filters,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    getInvocableTutorContracts = async (tutorIds?: string[]) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/invocable-tutor-contracts',
            data: { tutorIds },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    getTutorProductsStats = async (tutorId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/tutor-product-stats',
            data: { filter_filters: { tutorId } },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    generateTutorInvoices = async (values: any) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/generate-tutor-invoices',
            data: values,
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    getAgreementTemplate = async () => {
        this.response = await this.makeRequest({
            method: 'GET',
            url: ENDPOINT + '/get-tutor-agreement-template',
        }, true);
        return this;
    }

    getAgreementFields = async () => {
        this.response = await this.makeRequest({
            method: 'GET',
            url: ENDPOINT + '/get-tutor-agreement-fields',
        }, true);
        return this;
    }

    sendAgreementSignatureSms = async (tutorId: string, providingCorporationId: string, agreementContent: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/send-agreement-signature-sms',
            data: { tutorId, providingCorporationId, agreementContent },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    reorderTutorProducts = async (tutorId: string, tutorHasProductIds: string[]) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/reorder-products',
            data: { tutorId, tutorHasProductIds },
            headers: { "Content-Type": "application/json" }
        }, true);
        return this;
    }

    previewAgreementDocument = async (hash: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: '/preview-agreement-document',
            data: { hash },
            responseType: 'arraybuffer',
        }, false, true);
        return this;
    }

    signAgreementDocument = async (hash: string, agreementSignature: string) => {
        const formData = new FormData();
        formData.append("hash", hash);

        // Convierte la imagen de la firma (base64) a un archivo Blob
        const byteArray = atob(<string>agreementSignature?.split(',')[1]); // Elimina el prefijo 'data:image/png;base64,' y decodifica base64
        const arrayBuffer = new ArrayBuffer(byteArray.length);
        const uintArray = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteArray.length; i++) {
            uintArray[i] = byteArray.charCodeAt(i);
        }

        const blob = new Blob([uintArray], { type: "image/png" }); // Se asegura de que el archivo tenga el tipo correcto
        formData.append("agreementSignature", blob, "signature.png"); // El tercer parámetro es el nombre del archivo

        this.response = await this.makeRequest({
            method: 'POST',
            url: '/sign-agreement-document',
            data: formData,
            headers: { "Content-Type": "multipart/form-data" }
        }, false, true);

        return this;
    }

    downloadAgreementDocument = async (tutorId: string, providingCorporationId: string) => {
        this.response = await this.makeRequest({
            method: 'POST',
            url: ENDPOINT + '/download-agreement-document',
            data: { tutorId, providingCorporationId },
            responseType: 'arraybuffer',
        }, true);
        return this;
    }


    /*-------------------------------------------------------------
    * Tutor admin panel services
    --------------------------------------------------------------*/
    getMyProfile = async () => {
        this.response = await this.makeTutorRequest({
            method: 'GET',
            url: '/me',
        }, true);
        return this;
    }

    listMyDocuments = async (filters?: any) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/my-documents',
            data: filters,
        }, true);
        return this;
    }

    listMyInvoices = async (filters?: any) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/my-invoices',
            data: filters,
        }, true);
        return this;
    }

    generateInvoice = async (invoiceId: string) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/generate-invoice-pdf',
            data: { invoiceId },
            responseType: 'arraybuffer', // to avoid string conversion
        }, true);
        return this;
    }

    changeTutorPassword = async (password: string, passwordConfirm: string) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/change-password',
            data: { password, passwordConfirm }
        }, true);
        return this;
    }

    invocableContracts = async () => {
        this.response = await this.makeTutorRequest({
            method: 'GET',
            url: '/invocable-tutor-contracts',
        }, true);
        return this;
    }

    generateMyInvoices = async (values: any) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/generate-tutor-invoices',
            data: values,
        }, true);
        return this;
    }

    downloadMyAgreementDocument = async (tutorId: string, providingCorporationId: string) => {
        this.response = await this.makeTutorRequest({
            method: 'POST',
            url: '/download-agreement-document',
            data: { tutorId, providingCorporationId },
            responseType: 'arraybuffer',
        }, true);
        return this;
    }

}
